import React from 'react';
import { useHeader } from '../contexts/HeaderContext';

/**
 * Page-level context below the global top bar.
 * - title/subtitle (+ optional rightContent): Analytics-style page headings
 * - leftContent/rightContent: profile/review toolbars (identity + actions)
 *   kept out of the global chrome so search/workspace stay available
 */
const PageContextHeader: React.FC = () => {
  const { headerContent } = useHeader();
  const { leftContent, rightContent, title, subtitle, actions, hideBorder } =
    headerContent;

  // Custom page chrome (e.g. Overview title + needs-attention dashboards).
  if (leftContent) {
    return (
      <div
        className={`header-content sticky top-0 z-20 -mx-4 mb-2 animate-fade-in bg-background/95 px-4 py-2 backdrop-blur-sm sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 ${
          hideBorder ? '' : 'border-b border-border'
        }`}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <div className="min-w-0 flex-1">{leftContent}</div>
          {rightContent ? (
            <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
              {rightContent}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (!title && !subtitle && !rightContent && !actions) return null;

  // Title + subtitle · optional actions (center) · rightContent.
  if (rightContent || actions) {
    return (
      <div className="header-content sticky top-0 z-20 -mx-4 mb-2 animate-fade-in border-b border-border bg-background px-4 py-2 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
          <div className="min-w-0 flex-shrink-0 lg:pt-0.5">
            {title && (
              <h1 className="whitespace-nowrap text-3xl font-bold leading-none tracking-tight text-foreground sm:text-4xl">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mt-1 text-sm leading-snug text-foreground-secondary sm:text-base">
                {subtitle}
              </p>
            )}
          </div>
          {actions ? (
            <div className="min-w-0 flex-1 lg:max-w-4xl lg:px-1">
              {actions}
            </div>
          ) : null}
          {rightContent ? (
            <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
              {rightContent}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="header-content mb-5 animate-fade-in">
      {title && (
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {title}
        </h1>
      )}
      {subtitle && (
        <p className="mt-1 text-sm text-foreground-secondary sm:text-base">
          {subtitle}
        </p>
      )}
    </div>
  );
};

export default PageContextHeader;
